import { ReactNode } from 'react';
import { LimitCapture, LimitChartProps, LimitEntry, LimitGroup, LimitRow, LimitsProps, enteredOtherwise, entryName, followedLimits, limitCharts, limitListProblem, limitResults, secondsPerUnit, windowUnit } from '@/reports/limits';
import { formatNumber } from '@/reports/runs';
import { DataColumn, DataTable, LimitChart, StatusPill, SummaryGrid, useSheet, useSheetSection } from '../primitives';
import type { BlockViewProps } from './StandardBlockViews';

/**
 * How the limit blocks draw (their pure halves are src/reports/limits.ts):
 * each condition's values against their limits, the limits themselves, and
 * each condition's capture against the windows.
 */

const Empty = ({ children }: { children: ReactNode }) => <p className="small"><span className="unset">{children}</span></p>;

const fmt = (n: number | null): string => (n === null ? '—' : formatNumber(n).replace(/^-/, '−'));
const unitOf = (unit: string) => (unit ? ` (${unit})` : '');
const resultPill = (result: string) => <StatusPill state={result === 'PASS' ? 'pass' : result === 'FAIL' ? 'fail' : 'warn'}>{result}</StatusPill>;
const named = (entries: LimitEntry[]) => entries.some((e) => e.name && e.name !== e.label);
const windowed = (entries: LimitEntry[]) => entries.some((e) => e.from !== null || e.to !== null);

function resultColumns(group: LimitGroup, entries: LimitEntry[], props: LimitsProps): DataColumn<LimitRow>[] {
  const time = windowUnit(entries);
  const unit = unitOf(props.unit);
  const at = (n: number | null) => (n === null ? null : n * time.perSecond);
  const between = props.direction === 'between';
  // a lowest beside a highest prints as the range the run recorded
  const range = between && !!(props.lowValue.identifier || props.lowValue.role);
  return [
    { key: 'entry', title: 'Entry', render: (r) => (named(entries) ? <>{r.label}<span className="cell-detail">{r.name}</span></> : r.label) },
    ...(windowed(entries) ? [{ key: 'window', title: `Window (${time.unit})`, render: (r: LimitRow) => (r.from === null && r.to === null ? '—' : `${fmt(at(r.from))} – ${fmt(at(r.to))}`) }] : []),
    between
      ? { key: 'limit', title: `Limits${unit}`, render: (r) => `${fmt(r.lower)} – ${fmt(r.limit)}`, align: 'right' }
      : { key: 'limit', title: `Limit${unit}`, render: (r) => fmt(r.limit), align: 'right' },
    range
      ? { key: 'value', title: `Lowest – highest${unit}`, render: (r) => (r.low === null && r.value === null ? '—' : `${fmt(r.low)} – ${fmt(r.value)}`), align: 'right' }
      : { key: 'value', title: `Recorded${unit}`, render: (r) => fmt(r.value), align: 'right' },
    ...(group.rows.some((r) => r.captureMax !== null) ? [{ key: 'capture', title: `Capture peak${unit}`, render: (r: LimitRow) => fmt(r.captureMax), align: 'right' as const }] : []),
    { key: 'margin', title: 'Margin to limit (%)', render: (r) => fmt(r.marginPct), align: 'right' },
    { key: 'result', title: 'Result', render: (r) => resultPill(r.result) },
  ];
}

/** Each condition the run measured at, every entry judged against its limit; the most extreme value and the smallest margin first. */
export function LimitsView({ input }: BlockViewProps<LimitsProps>) {
  const m = limitResults(input);
  if (m.problem) return <Empty>{m.problem}</Empty>;
  if (m.groups.length === 0) return <Empty>Nothing the run recorded is at an entry of the limits.</Empty>;
  const unit = input.props.unit ? ` ${input.props.unit}` : '';
  const where = (x: { group: LimitGroup; row: LimitRow }) => [entryName(x.row), x.group.label].filter(Boolean).join(', ');
  const failed = m.groups.filter((g) => g.failed > 0).length;
  const title = input.props.title || 'Results against limits';
  return (
    <>
      {input.props.summary && m.worst && (
        <SummaryGrid items={[
          ...(m.extreme ? [{ label: m.upper ? 'Highest value' : 'Lowest value', value: `${fmt(m.extreme.row.value)}${unit} — ${where(m.extreme)} (limit ${fmt(m.extreme.row.limit)}${unit})` }] : []),
          { label: 'Smallest margin to a limit', value: `${fmt(m.worst.row.marginPct)} % — ${where(m.worst)}` },
          { label: 'Conditions', value: `${m.groups.length} measured · ${failed ? `${failed} with a value outside its limit` : 'every value within its limit'}` },
        ]} />
      )}
      {m.groups.map((g) => (
        <div key={g.key}>
          <DataTable
            title={g.label ? `${title} — ${g.label}` : title}
            numbered
            tableKey={`${input.block.id}-${g.key}`}
            columns={resultColumns(g, m.entries, input.props)}
            rows={g.rows}
            rowKey={(r) => r.key}
            rowState={(r) => (r.result === 'FAIL' ? 'fail' : undefined)}
          />
          {g.worst && m.groups.length > 1 && <p className="small">Smallest margin{g.label ? ` at ${g.label}` : ''}: {entryName(g.worst)}, {fmt(g.worst.marginPct)} % of its limit.</p>}
        </div>
      ))}
    </>
  );
}

/** The limits a `limits` block judges against, entry by entry. */
export function LimitListView({ input }: BlockViewProps<{ limits: string; title: string }>) {
  const followed = followedLimits(input);
  if (!followed) return <Empty>{limitListProblem(input)}</Empty>;
  const { model } = followed;
  if (model.problem) return <Empty>{model.problem}</Empty>;
  const { entries } = model;
  if (entries.length === 0) return <Empty>The run carries no list of limits.</Empty>;
  const time = windowUnit(entries);
  const unit = unitOf(followed.input.props.unit);
  const at = (n: number | null) => (n === null ? null : n * time.perSecond);
  // where the run entered limits of its own, the procedure's list beside them
  const entered = entries.some(enteredOtherwise);
  const between = followed.input.props.direction === 'between';
  return (
    <>
      <DataTable
        title={input.props.title || 'Limits'}
        numbered
        tableKey={input.block.id}
        columns={[
          { key: 'entry', title: 'Entry', render: (e: LimitEntry) => e.label },
          ...(named(entries) ? [{ key: 'name', title: 'Name', render: (e: LimitEntry) => e.name || '—' }] : []),
          ...(between
            ? [
                { key: 'lower', title: `Lower limit${unit}`, render: (e: LimitEntry) => fmt(e.lower), align: 'right' as const },
                { key: 'limit', title: `Upper limit${unit}`, render: (e: LimitEntry) => fmt(e.limit), align: 'right' as const },
                ...(entered ? [{ key: 'listed', title: `Procedure’s list${unit}`, render: (e: LimitEntry) => `${fmt(e.listedLower)} – ${fmt(e.listed)}`, align: 'right' as const }] : []),
              ]
            : [
                { key: 'limit', title: `Limit${unit}`, render: (e: LimitEntry) => fmt(e.limit), align: 'right' as const },
                ...(entered ? [{ key: 'listed', title: `Procedure’s list${unit}`, render: (e: LimitEntry) => fmt(e.listed), align: 'right' as const }] : []),
              ]),
          ...(windowed(entries)
            ? [
                { key: 'from', title: `Window start (${time.unit})`, render: (e: LimitEntry) => fmt(at(e.from)), align: 'right' as const },
                { key: 'to', title: `Window end (${time.unit})`, render: (e: LimitEntry) => fmt(at(e.to)), align: 'right' as const },
                { key: 'length', title: `Length (${time.unit})`, render: (e: LimitEntry) => fmt(e.from === null || e.to === null ? null : at(e.to - e.from)), align: 'right' as const },
              ]
            : []),
        ]}
        rows={entries}
        rowKey={(e) => e.key}
      />
      {entered && <p className="small unchanged-settings">The limits applied are the ones the run entered for each entry; where the procedure’s list gives another, it is shown beside them.</p>}
    </>
  );
}

/** One condition's capture against the limits, numbered as a figure with the capture as its source. */
function CaptureChart({ capture, entries, props, figureKey, xMax, caption }: { capture: LimitCapture; entries: LimitEntry[]; props: LimitChartProps; figureKey: string; xMax: number; caption: string }) {
  const sheet = useSheet();
  const section = useSheetSection();
  const trace = capture.trace;
  const file = capture.file;
  const entry = file ? sheet?.figure(figureKey, { caption, fileId: file.fileId, fileName: file.fileName, section: section?.title ?? null }) : undefined;
  const time = windowUnit(entries);
  const scale = secondsPerUnit(props.xUnit) * time.perSecond;
  const points = (trace?.points ?? []).map(([x, lo, hi]): [number, number, number] => [x * scale, lo, hi]);
  const at = (n: number) => n * time.perSecond;
  const label = (r: LimitRow) => r.name || r.label;
  const rows = capture.group.rows.filter((r) => r.from !== null && r.to !== null);
  const limits = rows.filter((r) => r.limit !== null).map((r) => ({ from: at(r.from as number), to: at(r.to as number), y: r.limit as number, label: label(r) }));
  // between two limits, the lower draws as a second staircase
  const lowerLimits = rows.filter((r) => r.lower !== null).map((r) => ({ from: at(r.from as number), to: at(r.to as number), y: r.lower as number }));
  const levels = rows.filter((r) => r.value !== null).map((r) => ({ from: at(r.from as number), to: at(r.to as number), y: r.value as number, label: label(r), state: (r.result === 'FAIL' ? 'fail' : 'pass') as 'pass' | 'fail' }));
  const series = props.seriesLabel || trace?.yLabel || 'Capture';
  return (
    <LimitChart
      id={entry?.anchor}
      points={points}
      seriesLabel={series}
      limits={limits}
      lowerLimits={lowerLimits}
      limitLabel="Limit"
      levels={levels}
      levelsLabel="Recorded value in window"
      xMin={0}
      xMax={xMax}
      xLabel={`Time (${time.unit})`}
      yLabel={series}
      ariaLabel={caption}
      caption={<>{entry && <b>Figure {entry.number}.</b>} {caption}{file ? ` Capture: ${file.fileName}.` : ''}</>}
    />
  );
}

/** Each condition's capture drawn against the windows and limits it was judged by, and again zoomed to the short windows. */
export function LimitChartView({ input }: BlockViewProps<LimitChartProps>) {
  const charts = limitCharts(input);
  if (charts.problem || !charts.model) return <Empty>{charts.problem}</Empty>;
  const { entries } = charts.model;
  const time = windowUnit(entries);
  const ends = entries.flatMap((e) => (e.to === null ? [] : [e.to * time.perSecond])).sort((a, b) => a - b);
  const what = input.props.title || 'Capture against the limits';
  return (
    <>
      {charts.captures.map((c) => {
        const where = c.group.label ? `, ${c.group.label}` : '';
        if (!c.trace) return <Empty key={c.group.key}>{c.file ? `The capture${where} could not be read, so it is not drawn.` : `The run holds no capture${where} to draw against the limits.`}</Empty>;
        const last = c.trace.points.length ? c.trace.points[c.trace.points.length - 1][0] * secondsPerUnit(input.props.xUnit) * time.perSecond : 0;
        const full = ends.length ? ends[ends.length - 1] : last;
        // the zoom covers the short windows: every window but the last, long one
        const zoom = ends.length > 1 ? ends[ends.length - 2] : full;
        const figure = `limit-chart-${input.block.id}-${c.group.key}`;
        return (
          <div key={c.group.key}>
            <CaptureChart capture={c} entries={entries} props={input.props} figureKey={`${figure}-full`} xMax={full} caption={`${what}${where}. Shaded bands mark the windows; the dashed line is the limit in each, and the solid one the value recorded there.`} />
            {input.props.zoom && zoom < full / 2 && (
              <CaptureChart capture={c} entries={entries} props={input.props} figureKey={`${figure}-zoom`} xMax={zoom} caption={`The same capture${where}, zoomed to the first ${fmt(zoom)} ${time.unit}, where the short windows are judged.`} />
            )}
          </div>
        );
      })}
    </>
  );
}
